import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check,
  Info,
  Ruler,
  Moon,
  Wine,
  Utensils,
  Briefcase,
  Users,
  Zap,
  Heart,
  Activity,
  Droplets,
  FlaskConical,
  Footprints
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';

const STEPS = [
  { id: 'basics', title: 'Basic Metrics', icon: Ruler },
  { id: 'activity', title: 'Activity Level', icon: Footprints },
  { id: 'sleep', title: 'Sleep Quality', icon: Moon },
  { id: 'nutrition', title: 'Nutrition', icon: Utensils },
  { id: 'alcohol', title: 'Alcohol', icon: Wine },
  { id: 'lifestyle', title: 'Stress & Lifestyle', icon: Briefcase },
  { id: 'history', title: 'Family History', icon: Users },
  { id: 'symptoms', title: 'Symptoms', icon: Zap },
  { id: 'biometrics', title: 'Biometrics', icon: Activity },
  { id: 'bloodtests', title: 'Blood Tests', icon: FlaskConical }
];

// Conversion helpers
const cmToFeetInches = (cm) => {
  if (!cm) return { feet: '', inches: '' };
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet: feet.toString(), inches: inches.toString() };
};

const feetInchesToCm = (feet, inches) => {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  return Math.round((f * 12 + i) * 2.54);
};

const kgToLbs = (kg) => {
  if (!kg) return '';
  return Math.round(kg * 2.205).toString();
};

const lbsToKg = (lbs) => {
  if (!lbs) return '';
  return Math.round(parseFloat(lbs) / 2.205);
};

const cmToInches = (cm) => {
  if (!cm) return '';
  return Math.round(cm / 2.54).toString();
};

const inchesToCm = (inches) => {
  if (!inches) return '';
  return Math.round(parseFloat(inches) * 2.54);
};

const QuestionnairePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [useMetric, setUseMetric] = useState(true);
  
  const [imperialValues, setImperialValues] = useState({
    heightFeet: '', heightInches: '', weightLbs: '', waistInches: ''
  });

  const [formData, setFormData] = useState({
    // Basic metrics
    age: '',
    height_cm: '',
    weight_kg: '',
    waist_cm: '',
    
    // Activity
    activity_days_per_week: 3,
    exercise_type: 'mixed',
    resistance_training_days: 1,
    daily_steps_estimate: '5000-7500',
    sedentary_hours: 8,
    
    // Sleep
    sleep_duration_hours: 7,
    sleep_quality: 3,
    snoring: false,
    sleep_apnea_symptoms: false,
    wake_during_night: 'rarely',
    screen_time_before_bed: 'often',
    
    // Nutrition
    late_eating_frequency: 'sometimes',
    ultra_processed_frequency: 'sometimes',
    meals_per_day: 3,
    protein_priority: 'sometimes',
    water_intake_liters: 2,
    caffeine_drinks_per_day: 2,
    
    // Alcohol
    alcohol_frequency: 'weekly',
    alcohol_units_per_session: 3,
    
    // Stress & Lifestyle
    work_hours_per_week: 45,
    perceived_stress: 5,
    meditation_practice: false,
    nature_exposure_weekly: 'sometimes',
    
    // Family history
    family_history_t2d: false,
    family_history_heart: false,
    family_history_hypertension: false,
    family_history_obesity: false,
    family_history_cancer: false,
    
    // Symptoms
    energy_level: 3,
    focus_level: 3,
    libido_level: 3,
    afternoon_crash: false,
    cravings_frequency: 'sometimes',
    brain_fog: false,
    joint_pain: false,
    frequent_urination: false,
    excessive_thirst: false,
    slow_wound_healing: false,
    mood_swings: false,
    
    // Biometrics (optional)
    resting_hr: '',
    hrv_ms: '',
    respiratory_rate: '',
    spo2: '',
    body_fat_percentage: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    
    // Blood tests (optional)
    blood_test_date: '',
    fasting_glucose: '',
    hba1c: '',
    total_cholesterol: '',
    ldl_cholesterol: '',
    hdl_cholesterol: '',
    triglycerides: '',
    alt: '',
    ast: '',
    creatinine: '',
    egfr: '',
    tsh: '',
    testosterone_total: '',
    testosterone_free: '',
    vitamin_d: '',
    crp: '',
    ferritin: '',
    vitamin_b12: ''
  });

  useEffect(() => {
    const saved = localStorage.getItem('trajectory_questionnaire_v2');
    const savedUnit = localStorage.getItem('trajectory_unit');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed);
        const { feet, inches } = cmToFeetInches(parsed.height_cm);
        setImperialValues({
          heightFeet: feet, heightInches: inches,
          weightLbs: kgToLbs(parsed.weight_kg),
          waistInches: cmToInches(parsed.waist_cm)
        });
      } catch (e) {}
    }
    if (savedUnit) setUseMetric(savedUnit === 'metric');
  }, []);

  useEffect(() => {
    localStorage.setItem('trajectory_questionnaire_v2', JSON.stringify(formData));
    localStorage.setItem('trajectory_unit', useMetric ? 'metric' : 'imperial');
  }, [formData, useMetric]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMetricChange = (field, value) => {
    handleChange(field, value);
    if (field === 'height_cm') {
      const { feet, inches } = cmToFeetInches(parseFloat(value));
      setImperialValues(prev => ({ ...prev, heightFeet: feet, heightInches: inches }));
    } else if (field === 'weight_kg') {
      setImperialValues(prev => ({ ...prev, weightLbs: kgToLbs(parseFloat(value)) }));
    } else if (field === 'waist_cm') {
      setImperialValues(prev => ({ ...prev, waistInches: cmToInches(parseFloat(value)) }));
    }
  };

  const handleImperialHeightChange = (feet, inches) => {
    setImperialValues(prev => ({ ...prev, heightFeet: feet, heightInches: inches }));
    const cm = feetInchesToCm(feet, inches);
    if (cm > 0) handleChange('height_cm', cm.toString());
  };

  const handleImperialWeightChange = (lbs) => {
    setImperialValues(prev => ({ ...prev, weightLbs: lbs }));
    const kg = lbsToKg(lbs);
    if (kg > 0) handleChange('weight_kg', kg.toString());
  };

  const handleImperialWaistChange = (inches) => {
    setImperialValues(prev => ({ ...prev, waistInches: inches }));
    const cm = inchesToCm(inches);
    if (cm > 0) handleChange('waist_cm', cm.toString());
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const validateStep = () => {
    if (currentStep === 0) {
      if (!formData.age || !formData.height_cm || !formData.weight_kg || !formData.waist_cm) {
        toast.error('Please fill in all required fields');
        return false;
      }
      if (formData.age < 18 || formData.age > 100) {
        toast.error('Please enter a valid age');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        age: parseInt(formData.age),
        height_cm: parseFloat(formData.height_cm),
        weight_kg: parseFloat(formData.weight_kg),
        waist_cm: parseFloat(formData.waist_cm),
        resting_hr: formData.resting_hr ? parseInt(formData.resting_hr) : null,
        hrv_ms: formData.hrv_ms ? parseInt(formData.hrv_ms) : null,
        respiratory_rate: formData.respiratory_rate ? parseInt(formData.respiratory_rate) : null,
        spo2: formData.spo2 ? parseInt(formData.spo2) : null,
        body_fat_percentage: formData.body_fat_percentage ? parseFloat(formData.body_fat_percentage) : null,
        blood_pressure_systolic: formData.blood_pressure_systolic ? parseInt(formData.blood_pressure_systolic) : null,
        blood_pressure_diastolic: formData.blood_pressure_diastolic ? parseInt(formData.blood_pressure_diastolic) : null,
        blood_test_date: formData.blood_test_date || null,
        fasting_glucose: formData.fasting_glucose ? parseFloat(formData.fasting_glucose) : null,
        hba1c: formData.hba1c ? parseFloat(formData.hba1c) : null,
        total_cholesterol: formData.total_cholesterol ? parseFloat(formData.total_cholesterol) : null,
        ldl_cholesterol: formData.ldl_cholesterol ? parseFloat(formData.ldl_cholesterol) : null,
        hdl_cholesterol: formData.hdl_cholesterol ? parseFloat(formData.hdl_cholesterol) : null,
        triglycerides: formData.triglycerides ? parseFloat(formData.triglycerides) : null,
        alt: formData.alt ? parseFloat(formData.alt) : null,
        ast: formData.ast ? parseFloat(formData.ast) : null,
        creatinine: formData.creatinine ? parseFloat(formData.creatinine) : null,
        egfr: formData.egfr ? parseFloat(formData.egfr) : null,
        tsh: formData.tsh ? parseFloat(formData.tsh) : null,
        testosterone_total: formData.testosterone_total ? parseFloat(formData.testosterone_total) : null,
        testosterone_free: formData.testosterone_free ? parseFloat(formData.testosterone_free) : null,
        vitamin_d: formData.vitamin_d ? parseFloat(formData.vitamin_d) : null,
        crp: formData.crp ? parseFloat(formData.crp) : null,
        ferritin: formData.ferritin ? parseFloat(formData.ferritin) : null,
        vitamin_b12: formData.vitamin_b12 ? parseFloat(formData.vitamin_b12) : null
      };

      // If user is not logged in, save to localStorage and redirect to register
      if (!user) {
        localStorage.setItem('fig_pending_questionnaire', JSON.stringify(submitData));
        toast.success('Assessment complete! Create an account to see your results.');
        navigate('/register?redirect=submit-questionnaire');
        return;
      }

      await api.post('/questionnaire/submit', submitData);
      localStorage.removeItem('trajectory_questionnaire_v2');
      await refreshUser();
      toast.success('Assessment complete!');
      navigate('/results');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const renderRadioOptions = (field, options, cols = 2) => (
    <RadioGroup
      value={formData[field]}
      onValueChange={(value) => handleChange(field, value)}
      className={`grid grid-cols-${cols} gap-3`}
    >
      {options.map((option) => (
        <div key={option.value} className="flex items-center">
          <RadioGroupItem value={option.value} id={`${field}-${option.value}`} className="peer sr-only" />
          <Label
            htmlFor={`${field}-${option.value}`}
            className="flex-1 cursor-pointer rounded-lg border border-[#D1D1CD] bg-[#EBEBE6] p-3 text-sm hover:bg-[#D1D1CD] peer-data-[state=checked]:border-[#2F3E30] peer-data-[state=checked]:bg-[#2F3E30]/10 transition-colors text-center"
          >
            {option.label}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0: // Basic Metrics
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-end gap-3 pb-4 border-b border-[#D1D1CD]">
              <span className={`text-sm ${useMetric ? 'text-[#525252]' : 'text-[#1A1C19] font-medium'}`}>Imperial</span>
              <Switch checked={useMetric} onCheckedChange={setUseMetric} data-testid="unit-toggle" />
              <span className={`text-sm ${useMetric ? 'text-[#1A1C19] font-medium' : 'text-[#525252]'}`}>Metric</span>
            </div>

            <div>
              <Label className="text-sm font-medium text-[#1A1C19]">Age *</Label>
              <Input type="number" value={formData.age} onChange={(e) => handleChange('age', e.target.value)}
                placeholder="45" className="mt-2 bg-white border-[#D1D1CD] h-12 text-[#1A1C19]" data-testid="questionnaire-age-input" />
            </div>

            {useMetric ? (
              <>
                <div>
                  <Label className="text-sm font-medium text-[#1A1C19]">Height (cm) *</Label>
                  <Input type="number" value={formData.height_cm} onChange={(e) => handleMetricChange('height_cm', e.target.value)}
                    placeholder="178" className="mt-2 bg-white border-[#D1D1CD] h-12 text-[#1A1C19]" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A1C19]">Weight (kg) *</Label>
                  <Input type="number" value={formData.weight_kg} onChange={(e) => handleMetricChange('weight_kg', e.target.value)}
                    placeholder="85" className="mt-2 bg-white border-[#D1D1CD] h-12 text-[#1A1C19]" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A1C19]">Waist Circumference (cm) *</Label>
                  <Input type="number" value={formData.waist_cm} onChange={(e) => handleMetricChange('waist_cm', e.target.value)}
                    placeholder="96" className="mt-2 bg-white border-[#D1D1CD] h-12 text-[#1A1C19]" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-sm font-medium text-[#1A1C19]">Height *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div className="relative">
                      <Input type="number" value={imperialValues.heightFeet}
                        onChange={(e) => handleImperialHeightChange(e.target.value, imperialValues.heightInches)}
                        placeholder="5" className="bg-white border-[#D1D1CD] h-12 pr-12 text-[#1A1C19]" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252] text-sm">ft</span>
                    </div>
                    <div className="relative">
                      <Input type="number" value={imperialValues.heightInches}
                        onChange={(e) => handleImperialHeightChange(imperialValues.heightFeet, e.target.value)}
                        placeholder="10" className="bg-white border-[#D1D1CD] h-12 pr-12 text-[#1A1C19]" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252] text-sm">in</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A1C19]">Weight (lbs) *</Label>
                  <Input type="number" value={imperialValues.weightLbs}
                    onChange={(e) => handleImperialWeightChange(e.target.value)}
                    placeholder="187" className="mt-2 bg-white border-[#D1D1CD] h-12 text-[#1A1C19]" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A1C19]">Waist Circumference (inches) *</Label>
                  <Input type="number" value={imperialValues.waistInches}
                    onChange={(e) => handleImperialWaistChange(e.target.value)}
                    placeholder="38" className="mt-2 bg-white border-[#D1D1CD] h-12 text-[#1A1C19]" />
                </div>
              </>
            )}

            <div className="p-4 bg-[#2F3E30]/10 border border-[#2F3E30]/20">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#2F3E30] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm mb-1 text-[#1A1C19]">How to measure waist</p>
                  <p className="text-[#525252] text-sm">Stand relaxed, breathe out. Measure at navel level, tape horizontal. Don't hold stomach in.</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 1: // Activity
        return (
          <div className="space-y-8">
            <div>
              <Label className="text-sm font-medium mb-4 block">Exercise days per week: {formData.activity_days_per_week}</Label>
              <Slider value={[formData.activity_days_per_week]} onValueChange={([v]) => handleChange('activity_days_per_week', v)}
                max={7} min={0} step={1} className="py-4" />
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Primary exercise type</Label>
              {renderRadioOptions('exercise_type', [
                { value: 'cardio', label: 'Cardio' },
                { value: 'strength', label: 'Strength' },
                { value: 'mixed', label: 'Mixed' },
                { value: 'none', label: 'None' }
              ])}
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Resistance/weight training days: {formData.resistance_training_days}</Label>
              <Slider value={[formData.resistance_training_days]} onValueChange={([v]) => handleChange('resistance_training_days', v)}
                max={7} min={0} step={1} className="py-4" />
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Estimated daily steps</Label>
              {renderRadioOptions('daily_steps_estimate', [
                { value: '<3000', label: '<3,000' },
                { value: '3000-5000', label: '3-5k' },
                { value: '5000-7500', label: '5-7.5k' },
                { value: '7500-10000', label: '7.5-10k' },
                { value: '>10000', label: '>10,000' }
              ], 3)}
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Hours sitting per day: {formData.sedentary_hours}</Label>
              <Slider value={[formData.sedentary_hours]} onValueChange={([v]) => handleChange('sedentary_hours', v)}
                max={16} min={2} step={1} className="py-4" />
            </div>
          </div>
        );

      case 2: // Sleep
        return (
          <div className="space-y-8">
            <div>
              <Label className="text-sm font-medium mb-4 block">Sleep duration: {formData.sleep_duration_hours} hours</Label>
              <Slider value={[formData.sleep_duration_hours]} onValueChange={([v]) => handleChange('sleep_duration_hours', v)}
                max={12} min={4} step={0.5} className="py-4" />
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Sleep quality: {formData.sleep_quality}/5</Label>
              <Slider value={[formData.sleep_quality]} onValueChange={([v]) => handleChange('sleep_quality', v)}
                max={5} min={1} step={1} className="py-4" />
              <div className="flex justify-between text-xs text-[#525252]"><span>Poor</span><span>Excellent</span></div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD]">
                <Checkbox id="snoring" checked={formData.snoring} onCheckedChange={(c) => handleChange('snoring', c)} />
                <Label htmlFor="snoring" className="cursor-pointer">I snore regularly</Label>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD]">
                <Checkbox id="sleep_apnea" checked={formData.sleep_apnea_symptoms} onCheckedChange={(c) => handleChange('sleep_apnea_symptoms', c)} />
                <Label htmlFor="sleep_apnea" className="cursor-pointer">I have sleep apnea symptoms (gasping, stopping breathing)</Label>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Wake during night</Label>
              {renderRadioOptions('wake_during_night', [
                { value: 'never', label: 'Never' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' }
              ])}
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Screen time before bed</Label>
              {renderRadioOptions('screen_time_before_bed', [
                { value: 'never', label: 'Never' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' },
                { value: 'always', label: 'Always' }
              ], 3)}
            </div>
          </div>
        );

      case 3: // Nutrition
        return (
          <div className="space-y-8">
            <div>
              <Label className="text-sm font-medium mb-3 block">How often do you eat after 8pm?</Label>
              {renderRadioOptions('late_eating_frequency', [
                { value: 'never', label: 'Never' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' },
                { value: 'always', label: 'Always' }
              ])}
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Ultra-processed food frequency</Label>
              {renderRadioOptions('ultra_processed_frequency', [
                { value: 'never', label: 'Never' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' },
                { value: 'daily', label: 'Daily' }
              ], 3)}
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Meals per day: {formData.meals_per_day}</Label>
              <Slider value={[formData.meals_per_day]} onValueChange={([v]) => handleChange('meals_per_day', v)}
                max={6} min={1} step={1} className="py-4" />
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Protein with each meal</Label>
              {renderRadioOptions('protein_priority', [
                { value: 'never', label: 'Never' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' },
                { value: 'always', label: 'Always' }
              ])}
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Water intake: {formData.water_intake_liters}L per day</Label>
              <Slider value={[formData.water_intake_liters]} onValueChange={([v]) => handleChange('water_intake_liters', v)}
                max={5} min={0.5} step={0.5} className="py-4" />
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Caffeinated drinks per day: {formData.caffeine_drinks_per_day}</Label>
              <Slider value={[formData.caffeine_drinks_per_day]} onValueChange={([v]) => handleChange('caffeine_drinks_per_day', v)}
                max={10} min={0} step={1} className="py-4" />
            </div>
          </div>
        );

      case 4: // Alcohol
        return (
          <div className="space-y-8">
            <div>
              <Label className="text-sm font-medium mb-3 block">Alcohol frequency</Label>
              {renderRadioOptions('alcohol_frequency', [
                { value: 'never', label: 'Never' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'daily', label: 'Daily' }
              ])}
            </div>
            {formData.alcohol_frequency !== 'never' && (
              <div>
                <Label className="text-sm font-medium mb-4 block">Units per session: {formData.alcohol_units_per_session}</Label>
                <Slider value={[formData.alcohol_units_per_session]} onValueChange={([v]) => handleChange('alcohol_units_per_session', v)}
                  max={15} min={1} step={1} className="py-4" />
                <p className="text-xs text-[#525252] mt-2">1 unit = small wine, half pint, or single spirit</p>
              </div>
            )}
          </div>
        );

      case 5: // Stress & Lifestyle
        return (
          <div className="space-y-8">
            <div>
              <Label className="text-sm font-medium mb-4 block">Work hours per week: {formData.work_hours_per_week}</Label>
              <Slider value={[formData.work_hours_per_week]} onValueChange={([v]) => handleChange('work_hours_per_week', v)}
                max={80} min={20} step={5} className="py-4" />
            </div>

            <div>
              <Label className="text-sm font-medium mb-4 block">Stress level: {formData.perceived_stress}/10</Label>
              <Slider value={[formData.perceived_stress]} onValueChange={([v]) => handleChange('perceived_stress', v)}
                max={10} min={1} step={1} className="py-4" />
              <div className="flex justify-between text-xs text-[#525252]"><span>Minimal</span><span>Extreme</span></div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD]">
              <Checkbox id="meditation" checked={formData.meditation_practice} onCheckedChange={(c) => handleChange('meditation_practice', c)} />
              <Label htmlFor="meditation" className="cursor-pointer">I practice meditation or mindfulness regularly</Label>
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Time in nature weekly</Label>
              {renderRadioOptions('nature_exposure_weekly', [
                { value: 'never', label: 'Never' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' }
              ])}
            </div>
          </div>
        );

      case 6: // Family History
        return (
          <div className="space-y-4">
            <p className="text-[#525252] text-sm mb-4">Select conditions in immediate family (parents, siblings):</p>
            {[
              { id: 'family_history_t2d', label: 'Type 2 Diabetes', desc: 'Parent or sibling with T2D' },
              { id: 'family_history_heart', label: 'Heart Disease', desc: 'Heart attack, stroke, CVD' },
              { id: 'family_history_hypertension', label: 'Hypertension', desc: 'High blood pressure' },
              { id: 'family_history_obesity', label: 'Obesity', desc: 'Significant weight issues' },
              { id: 'family_history_cancer', label: 'Cancer', desc: 'Any cancer diagnosis' }
            ].map(item => (
              <div key={item.id} className="flex items-center gap-4 p-4 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD]">
                <Checkbox id={item.id} checked={formData[item.id]} onCheckedChange={(c) => handleChange(item.id, c)} />
                <Label htmlFor={item.id} className="cursor-pointer flex-1">
                  <span className="font-medium">{item.label}</span>
                  <p className="text-sm text-[#525252]">{item.desc}</p>
                </Label>
              </div>
            ))}
          </div>
        );

      case 7: // Symptoms
        return (
          <div className="space-y-8">
            <div>
              <Label className="text-sm font-medium mb-4 block">Energy level: {formData.energy_level}/5</Label>
              <Slider value={[formData.energy_level]} onValueChange={([v]) => handleChange('energy_level', v)} max={5} min={1} step={1} className="py-4" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-4 block">Focus/concentration: {formData.focus_level}/5</Label>
              <Slider value={[formData.focus_level]} onValueChange={([v]) => handleChange('focus_level', v)} max={5} min={1} step={1} className="py-4" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-4 block">Libido: {formData.libido_level}/5</Label>
              <Slider value={[formData.libido_level]} onValueChange={([v]) => handleChange('libido_level', v)} max={5} min={1} step={1} className="py-4" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">Sugar/carb cravings</Label>
              {renderRadioOptions('cravings_frequency', [
                { value: 'never', label: 'Never' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'often', label: 'Often' },
                { value: 'always', label: 'Always' }
              ])}
            </div>
            <div className="space-y-3">
              <p className="text-sm font-medium">Do you experience any of these?</p>
              {[
                { id: 'afternoon_crash', label: 'Afternoon energy crashes' },
                { id: 'brain_fog', label: 'Brain fog or mental cloudiness' },
                { id: 'mood_swings', label: 'Mood swings or irritability' },
                { id: 'joint_pain', label: 'Joint pain or stiffness' },
                { id: 'frequent_urination', label: 'Frequent urination' },
                { id: 'excessive_thirst', label: 'Excessive thirst' },
                { id: 'slow_wound_healing', label: 'Slow wound healing' }
              ].map(item => (
                <div key={item.id} className="flex items-center gap-4 p-3 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD]">
                  <Checkbox id={item.id} checked={formData[item.id]} onCheckedChange={(c) => handleChange(item.id, c)} />
                  <Label htmlFor={item.id} className="cursor-pointer text-sm">{item.label}</Label>
                </div>
              ))}
            </div>
          </div>
        );

      case 8: // Biometrics
        return (
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-6">
              <p className="text-sm text-[#F59E0B]">Optional: Add data from wearables or recent measurements for a more accurate score.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Resting HR (bpm)</Label>
                <Input type="number" value={formData.resting_hr} onChange={(e) => handleChange('resting_hr', e.target.value)}
                  placeholder="65" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
              <div>
                <Label className="text-xs">HRV (ms)</Label>
                <Input type="number" value={formData.hrv_ms} onChange={(e) => handleChange('hrv_ms', e.target.value)}
                  placeholder="45" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
              <div>
                <Label className="text-xs">Respiratory Rate</Label>
                <Input type="number" value={formData.respiratory_rate} onChange={(e) => handleChange('respiratory_rate', e.target.value)}
                  placeholder="14" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
              <div>
                <Label className="text-xs">SpO2 (%)</Label>
                <Input type="number" value={formData.spo2} onChange={(e) => handleChange('spo2', e.target.value)}
                  placeholder="98" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
              <div>
                <Label className="text-xs">Body Fat %</Label>
                <Input type="number" value={formData.body_fat_percentage} onChange={(e) => handleChange('body_fat_percentage', e.target.value)}
                  placeholder="22" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
              <div>
                <Label className="text-xs">Systolic BP</Label>
                <Input type="number" value={formData.blood_pressure_systolic} onChange={(e) => handleChange('blood_pressure_systolic', e.target.value)}
                  placeholder="120" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
              <div>
                <Label className="text-xs">Diastolic BP</Label>
                <Input type="number" value={formData.blood_pressure_diastolic} onChange={(e) => handleChange('blood_pressure_diastolic', e.target.value)}
                  placeholder="80" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
              </div>
            </div>
          </div>
        );

      case 9: // Blood Tests
        return (
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-[#2F3E30]/10 border border-[#2F3E30]/20 mb-6">
              <p className="text-sm text-[#2F3E30]">Optional: Enter recent blood test results for the most comprehensive analysis.</p>
            </div>
            
            <div>
              <Label className="text-xs">Test Date</Label>
              <Input type="date" value={formData.blood_test_date} onChange={(e) => handleChange('blood_test_date', e.target.value)}
                className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
            </div>

            <div className="border-t border-[#D1D1CD] pt-4">
              <h4 className="font-medium text-sm mb-3">Glucose Markers</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Fasting Glucose (mg/dL)</Label>
                  <Input type="number" value={formData.fasting_glucose} onChange={(e) => handleChange('fasting_glucose', e.target.value)}
                    placeholder="95" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">HbA1c (%)</Label>
                  <Input type="number" step="0.1" value={formData.hba1c} onChange={(e) => handleChange('hba1c', e.target.value)}
                    placeholder="5.4" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
              </div>
            </div>

            <div className="border-t border-[#D1D1CD] pt-4">
              <h4 className="font-medium text-sm mb-3">Lipid Panel</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Total Cholesterol</Label>
                  <Input type="number" value={formData.total_cholesterol} onChange={(e) => handleChange('total_cholesterol', e.target.value)}
                    placeholder="180" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">LDL</Label>
                  <Input type="number" value={formData.ldl_cholesterol} onChange={(e) => handleChange('ldl_cholesterol', e.target.value)}
                    placeholder="100" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">HDL</Label>
                  <Input type="number" value={formData.hdl_cholesterol} onChange={(e) => handleChange('hdl_cholesterol', e.target.value)}
                    placeholder="55" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">Triglycerides</Label>
                  <Input type="number" value={formData.triglycerides} onChange={(e) => handleChange('triglycerides', e.target.value)}
                    placeholder="120" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
              </div>
            </div>

            <div className="border-t border-[#D1D1CD] pt-4">
              <h4 className="font-medium text-sm mb-3">Hormones & Inflammation</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Testosterone Total (ng/dL)</Label>
                  <Input type="number" value={formData.testosterone_total} onChange={(e) => handleChange('testosterone_total', e.target.value)}
                    placeholder="550" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">TSH (mIU/L)</Label>
                  <Input type="number" step="0.1" value={formData.tsh} onChange={(e) => handleChange('tsh', e.target.value)}
                    placeholder="2.0" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">CRP (mg/L)</Label>
                  <Input type="number" step="0.1" value={formData.crp} onChange={(e) => handleChange('crp', e.target.value)}
                    placeholder="0.8" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">Vitamin D (ng/mL)</Label>
                  <Input type="number" value={formData.vitamin_d} onChange={(e) => handleChange('vitamin_d', e.target.value)}
                    placeholder="45" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
              </div>
            </div>

            <div className="border-t border-[#D1D1CD] pt-4">
              <h4 className="font-medium text-sm mb-3">Liver & Kidney</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">ALT (U/L)</Label>
                  <Input type="number" value={formData.alt} onChange={(e) => handleChange('alt', e.target.value)}
                    placeholder="25" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">AST (U/L)</Label>
                  <Input type="number" value={formData.ast} onChange={(e) => handleChange('ast', e.target.value)}
                    placeholder="22" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">Creatinine (mg/dL)</Label>
                  <Input type="number" step="0.1" value={formData.creatinine} onChange={(e) => handleChange('creatinine', e.target.value)}
                    placeholder="0.9" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
                <div>
                  <Label className="text-xs">eGFR</Label>
                  <Input type="number" value={formData.egfr} onChange={(e) => handleChange('egfr', e.target.value)}
                    placeholder="95" className="mt-1 bg-[#EBEBE6] border-[#D1D1CD] h-10" />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FigIcon className="w-6 h-6 text-[#2F3E30]" />
              <span className="font-bold text-lg hidden sm:block text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
            </div>
            <span className="text-sm text-[#525252]">{currentStep + 1} / {STEPS.length}</span>
          </div>
          <Progress value={progress} className="mt-4 h-1 bg-[#D1D1CD]" />
        </div>
      </header>

      <main className="pt-28 pb-40 sm:pb-32 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              {React.createElement(STEPS[currentStep].icon, { className: 'w-5 h-5 text-[#2F3E30]' })}
              <h1 className="text-xl sm:text-2xl font-bold text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                {STEPS[currentStep].title.toUpperCase()}
              </h1>
            </div>
          </div>
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-4 sm:p-6 lg:p-8">{renderStep()}</div>
        </div>
      </main>

      <footer className="fixed left-0 right-0 z-50 bg-[#E8E4DC] border-t border-[#D1D1CD] bottom-0 pb-safe">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}
              className="border-[#D1D1CD] text-[#525252] hover:text-[#1A1C19] hover:bg-[#EBEBE6] flex-shrink-0 h-12 min-w-[80px] sm:min-w-[100px] uppercase tracking-wide" 
              data-testid="questionnaire-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /><span>Back</span>
            </Button>
            <Button onClick={handleNext} disabled={loading}
              className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white px-6 sm:px-8 flex-shrink-0 min-w-[120px] h-12 uppercase tracking-wide"
              data-testid="questionnaire-next-btn">
              {loading ? 'Submitting...' : currentStep === STEPS.length - 1 ? 'Get My Score' : 'Continue'}
              {!loading && currentStep < STEPS.length - 1 && <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />}
              {!loading && currentStep === STEPS.length - 1 && <Check className="w-4 h-4 ml-1 sm:ml-2" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QuestionnairePage;
